import React from "react";
import { NavLink } from "react-router-dom";
import theme from "../../utils/themeColors";

/**
 * Button variants
 */
export enum ButtonVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  TERTIARY = "tertiary",
  DANGER = "danger",
  SUCCESS = "success",
  WARNING = "warning",
  INFO = "info",
}

/**
 * Button sizes
 */
export enum ButtonSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

/**
 * Element types that the Button component can render as
 */
export type ButtonElementType =
  | "button"
  | "a"
  | "navlink"
  | React.ComponentType<any>;

/**
 * Base button props interface
 */
type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  as?: ButtonElementType;
  to?: string; // For NavLink
  href?: string; // For anchor
  className?: string;
  children?: React.ReactNode;
  [key: string]: any; // Para otras propiedades
};

/**
 * Button props interface
 */
export type ButtonProps = BaseButtonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps>;

/**
 * Button component
 *
 * A reusable button component with different variants, sizes, and states.
 * Can be rendered as different elements using the 'as' prop.
 *
 * @example
 * // As a button (default)
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // As an anchor
 * <Button as="a" href="/dashboard">Go to Dashboard</Button>
 *
 * // As a NavLink (React Router)
 * <Button as="navlink" to="/profile">Profile</Button>
 */
export const Button: React.FC<ButtonProps> = ({
  variant = ButtonVariant.PRIMARY,
  size = ButtonSize.MEDIUM,
  isLoading = false,
  loadingText = "Loading...",
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  className = "",
  disabled,
  as,
  to,
  href,
  ...props
}) => {
  // Get button style based on variant
  const getVariantStyle = () => {
    switch (variant) {
      case ButtonVariant.PRIMARY:
        return `${theme.button.primary}`;
      case ButtonVariant.SECONDARY:
        return "btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white";
      case ButtonVariant.TERTIARY:
        return "btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 dark:text-gray-500";
      case ButtonVariant.DANGER:
        return `${theme.button.danger} text-white`;
      case ButtonVariant.SUCCESS:
        return theme.button.success;
      case ButtonVariant.WARNING:
        return theme.button.warning;
      case ButtonVariant.INFO:
        return theme.button.info;
      default:
        return theme.button.primary;
    }
  };

  // Get button size style
  const getSizeStyle = () => {
    switch (size) {
      case ButtonSize.SMALL:
        return "px-3 py-1.5 text-sm";
      case ButtonSize.MEDIUM:
        return "px-5 py-2";
      case ButtonSize.LARGE:
        return "px-6 py-3 text-lg";
      default:
        return "px-5 py-2";
    }
  };

  // Combine all classes
  const buttonClasses = `
    ${getVariantStyle()}
    ${getSizeStyle()}
    ${fullWidth ? "w-full" : ""}
    ${isLoading || disabled ? "opacity-70 cursor-not-allowed" : ""}
    ${className}
    flex items-center justify-center gap-2 rounded-3xl
  `.trim();

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin fill-current shrink-0"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
    </svg>
  );

  // Content to render inside the button
  const content = isLoading ? (
    <>
      <LoadingSpinner />
      <span>{loadingText}</span>
    </>
  ) : (
    <>
      {leftIcon && <span className="shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </>
  );

  // Render the appropriate element based on the 'as' prop
  if (as === "a") {
    return (
      <a
        className={buttonClasses}
        href={href}
        {...(isLoading || disabled
          ? {
              "aria-disabled": "true",
              className: `${buttonClasses} pointer-events-none`,
            }
          : {})}
        {...props}
      >
        {content}
      </a>
    );
  }

  if (as === "navlink") {
    return (
      <NavLink
        to={to || ""}
        className={({ isActive }) =>
          `${buttonClasses} ${isActive ? "active" : ""}`
        }
        {...(isLoading || disabled
          ? {
              "aria-disabled": "true",
              className: `${buttonClasses} pointer-events-none`,
            }
          : {})}
        {...props}
      >
        {content}
      </NavLink>
    );
  }

  // Default to button
  return (
    <button
      className={buttonClasses}
      disabled={isLoading || disabled}
      {...props}
    >
      {content}
    </button>
  );
};

/**
 * Refresh Button component
 *
 * A specialized button for refresh actions with loading state.
 * Can be rendered as different elements using the 'as' prop.
 *
 * @example
 * // As a button (default)
 * <RefreshButton onClick={handleRefresh} />
 *
 * // As an anchor
 * <RefreshButton as="a" href="/refresh" />
 *
 * // As a NavLink (React Router)
 * <RefreshButton as="navlink" to="/refresh" />
 */
export interface RefreshButtonProps
  extends Omit<ButtonProps, "leftIcon" | "rightIcon"> {
  defaultText?: string;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  isLoading = false,
  onClick,
  className = `${theme.button.primary} w-[110px]`,
  loadingText = "Loading",
  defaultText = "Refresh",
  as,
  to,
  href,
  ...props
}) => {
  // Refresh icon SVG
  const RefreshIconSvg = () => (
    <svg
      className="fill-current shrink-0"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path d="M4.3 4.5c1.9-1.9 5.1-1.9 7 0 .7.7 1.2 1.7 1.4 2.7l2-.3c-.2-1.5-.9-2.8-1.9-3.8C10.1.4 5.7.4 2.9 3.1L.7.9 0 7.3l6.4-.7-2.1-2.1zM15.6 8.7l-6.4.7 2.1 2.1c-1.9 1.9-5.1 1.9-7 0-.7-.7-1.2-1.7-1.4-2.7l-2 .3c.2 1.5.9 2.8 1.9 3.8 1.4 1.4 3.1 2 4.9 2 1.8 0 3.6-.7 4.9-2l2.2 2.2.8-6.4z" />
    </svg>
  );

  // Use the main Button component with refresh icon
  return (
    <Button
      as={as}
      to={to}
      href={href}
      isLoading={isLoading}
      onClick={onClick}
      className={className}
      loadingText={loadingText}
      leftIcon={!isLoading ? <RefreshIconSvg /> : undefined}
      {...props}
    >
      {defaultText}
    </Button>
  );
};

export default Button;
